import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const hiringProcessData = [
    {
        id: 1,
        question: "I can't find the role I've been asked to apply for. How can I find it and apply on the website?",
        answer: "Start by searching for the role using keywords on our Careers page. If you can’t find the position, reach out to your contact for the job ID. Once you have it, simply search using the job ID to locate the role and submit your application."
    },
    {
        id: 2,
        question: "I forgot my username/password credentials. How can I get them back?",
        answer: "Forgot your password? No worries. Simply click the 'Forgot Password' link on the login page, and we'll send password reset instructions directly to your registered email address so you can quickly regain access to your account."
    },
    {
        id: 3,
        question: "How long does it take to apply?",
        answer: "Applying to Mobikode is quick and easy, it usually takes just 5–10 minutes. If you already have your resume and portfolio handy, our Quick Apply option makes the process even faster."
    },
    {
        id: 4,
        question: "How long will it take to find out if I'm moving ahead in the hiring process?",
        answer: "Our hiring team reviews applications regularly and strives to keep the process moving smoothly. You can usually expect to hear back from us within 1–2 weeks regarding the next steps. Thank you for your interest in joining our team!"
    }
];

const HiringProcessFAQ = () => {
    const [openId, setOpenId] = useState(null);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.12 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    return (
        <section className="w-full pb-[40px] md:pb-[100px] bg-white select-none">
            <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">
                <div className="flex flex-col lg:flex-row items-start gap-[24px] md:gap-[40px] lg:gap-[80px]">

                    {/* Left Sticky Header */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="w-full lg:w-4/12 lg:sticky lg:top-[120px]"
                    >
                        <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight leading-[1.2]">
                            Understanding the hiring process
                        </h2>
                    </motion.div>

                    {/* FAQ Items Container */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                        className="w-full lg:w-8/12 flex flex-col gap-[16px] md:gap-[20px]"
                    >
                        {hiringProcessData.map((item) => {
                            const isOpen = openId === item.id;

                            return (
                                <motion.div
                                    key={item.id}
                                    variants={itemVariants}
                                    onClick={() => setOpenId(isOpen ? null : item.id)}
                                    onMouseEnter={() => window.innerWidth >= 1024 && setOpenId(item.id)}
                                    onMouseLeave={() => window.innerWidth >= 1024 && setOpenId(null)}
                                    className="group relative bg-white border border-[#E4E8F5] p-[20px] sm:p-[24px] md:p-[40px] cursor-pointer transition-all duration-300 md:hover:shadow-[0_10px_30px_rgba(1,56,106,0.08)] rounded-sm overflow-hidden"
                                >
                                    <h3 className="font-sora text-[16px] md:text-[20px] font-[600] text-[#01386A] transition-colors duration-300 pr-[20px]">
                                        {item.question}
                                    </h3>

                                    {/* Smooth Accordion Animation */}
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ 
                                                    height: "auto", 
                                                    opacity: 1,
                                                    transition: {
                                                        height: { duration: 0.35, ease: [0.04, 0.62, 0.23, 0.98] },
                                                        opacity: { duration: 0.25, delay: 0.1 }
                                                    }
                                                }}
                                                exit={{ 
                                                    height: 0, 
                                                    opacity: 0,
                                                    transition: {
                                                        height: { duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] },
                                                        opacity: { duration: 0.15 }
                                                    }
                                                }}
                                                className="overflow-hidden"
                                            >
                                                <p className="font-graphik text-[#6C7689] text-[14px] md:text-[16px] font-[500] leading-[1.6] pt-[12px] md:pt-[16px]">
                                                    {item.answer}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {/* Bottom Animated Border Highlight */}
                                    <div className="absolute bottom-0 left-0 w-full h-[3px] bg-[#01386A] z-20"></div>
                                    <div className={`absolute bottom-0 left-0 h-[3px] bg-[#ED6800] transition-all duration-500 ease-out z-30 ${isOpen ? 'w-full' : 'w-0 group-hover:w-full'}`}></div>
                                </motion.div>
                            );
                        })}
                    </motion.div>

                </div>
            </div>
        </section>
    );
};

export default HiringProcessFAQ;